import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Crown, Star, Shield, Phone, Eye, BarChart3, 
  CheckCircle, XCircle, Clock, ArrowRight, 
  CreditCard, Calendar, Users, Zap, Settings, Info, ShieldCheck, X
} from 'lucide-react';
import VerifiedBadge from '../common/VerifiedBadge';
import MembershipTierBadge from '../common/MembershipTierBadge';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../utils/api';

interface MembershipInfo {
  id: number;
  first_name: string;
  last_name: string;
  phone_visible_to_customers: boolean;
  verification_status: 'pending' | 'verified' | 'rejected';
  services_used: number;
  membership_status: 'active' | 'expired' | 'cancelled';
  membership_start_date: string;
  membership_end_date: string;
  membership_tier_name: string;
  membership_tier_display: string;
  service_limit: number;
  phone_visible: boolean;
  verified_badge: boolean;
  priority_support: boolean;
  enhanced_visibility: boolean;
  services_remaining: string;
  membership_status_display: string;
  cancel_at_period_end?: boolean;
}

interface MembershipTier {
  id: number;
  name: string;
  display_name: string;
  description: string;
  price: number;
  duration_months: number;
  service_limit: number;
  phone_visible: boolean;
  verified_badge: boolean;
  priority_support: boolean;
  enhanced_visibility: boolean;
  features: string[];
  currency?: string;
  paddle_price_id?: string;
  billing_interval?: string;
}

const getCurrencySymbol = (currencyCode: string): string => {
  const code = (currencyCode || 'USD').toUpperCase();
  const symbols: { [key: string]: string } = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', AUD: 'A$', CAD: 'C$',
    CHF: 'CHF', HKD: 'HK$', SGD: 'S$', SEK: 'kr', ARS: '$', BRL: 'R$',
    CLP: '$', CNY: '¥', COP: '$', CZK: 'Kč', DKK: 'kr', HUF: 'Ft',
    ILS: '₪', INR: '₹', KRW: '₩', MXN: '$', NOK: 'kr', NZD: 'NZ$',
    PEN: 'S/.', PLN: 'zł', RUB: '₽', THB: '฿', TRY: '₺', TWD: 'NT$',
    UAH: '₴', VND: '₫', ZAR: 'R'
  };
  return symbols[code] || '$';
};

interface MembershipHistoryItem {
  id: number;
  transactionId: string;
  planName: string;
  amount: number;
  currency?: string;
  paymentMethod: string;
  status: string;
  startDate: string;
  endDate: string;
  createdAt: string;
  billingInterval?: string;
}

const styles = `
  /* Brand color styles for Membership page */
  .pricing-page {
    padding: 40px 0;
    background-color: #f8fafc;
    min-height: 80vh;
  }
  
  .pricing-page h1 {
    background: linear-gradient(to right, #FD2692, #185FA5);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 38px;
    font-weight: 800 !important;
    margin-bottom: 8px;
  }
  
  .pricing-page p.text-muted {
    font-size: 16px;
    color: #64748b !important;
    margin-bottom: 40px;
  }
  
  .membership-status-card {
    background: #ffffff !important;
    border-radius: 16px !important;
    border: 1px solid #e2e8f0 !important;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05) !important;
    padding: 30px !important;
    margin-bottom: 40px !important;
  }
  
  .membership-status-card h3 {
    font-size: 20px !important;
    font-weight: 700 !important;
    color: #1e293b !important;
    margin-bottom: 24px !important;
  }
  
  .membership-stat-box {
    background: #f8fafc !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 12px !important;
    padding: 20px !important;
    transition: all 0.3s ease !important;
  }
  
  .membership-stat-box:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    border-color: rgba(24, 95, 165, 0.2) !important;
  }
  
  .stat-val-primary {
    color: #185FA5 !important;
    font-weight: 700;
  }
  
  .stat-val-success {
    color: #16a34a !important;
    font-weight: 700;
  }
  
  .stat-val-warning {
    color: #EF9F27 !important;
    font-weight: 700;
  }
  
  .membership-info-box {
    background-color: #E6F1FB !important;
    border-left: 4px solid #185FA5 !important;
    border-radius: 0 12px 12px 0 !important;
    padding: 20px !important;
    margin-top: 24px !important;
  }
  
  .membership-info-box strong {
    color: #0E3A61;
  }
  
  .pricing-page h3.text-center {
    font-size: 24px !important;
    font-weight: 700 !important;
    color: #1e293b !important;
    margin-bottom: 30px !important;
  }
  
  .pricing-card.popular {
    border: 1px solid rgba(24, 95, 165, 0.15) !important;
    box-shadow: 0 10px 30px -5px rgba(24, 95, 165, 0.1) !important;
    border-radius: 20px !important;
    overflow: hidden !important;
    transition: all 0.3s ease !important;
    background: #fff !important;
    color: #000 !important;
  }
  
  .pricing-card.popular:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px -10px rgba(24, 95, 165, 0.2) !important;
    border-color: rgba(24, 95, 165, 0.3) !important;
  }
  
  .pricing-card.popular h5 {
    color: #1e293b !important;
    font-weight: 700 !important;
    font-size: 20px !important;
  }
  
  .pricing-card.popular h2 {
    color: #185FA5 !important;
    font-size: 42px !important;
    margin: 15px 0 5px !important;
    font-weight: 800 !important;
  }
  
  .pricing-card.popular ul li {
    font-size: 15px;
    color: #475569;
    margin-bottom: 12px;
  }
  
  .brand-btn-gradient {
    background: linear-gradient(to right, #FD2692, #185FA5) !important;
    border: none !important;
    color: white !important;
    font-weight: 700 !important;
    padding: 14px 28px !important;
    border-radius: 10px !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 4px 15px rgba(24, 95, 165, 0.2) !important;
  }
  
  .brand-btn-gradient:hover {
    background: linear-gradient(to right, #185FA5, #FD2692) !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(24, 95, 165, 0.35) !important;
  }
  
  .pricing-card .alert-success {
    background-color: #E6F1FB !important;
    color: #0E3A61 !important;
    border: 1px solid rgba(24, 95, 165, 0.2) !important;
    border-radius: 12px !important;
    padding: 15px !important;
    margin-top: 15px !important;
  }
  
  /* Upgrade Modal */
  .upgrade-modal .modal-content {
    border-radius: 20px !important;
    border: none !important;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
  }
  
  .upgrade-modal .modal-header {
    border-bottom: 1px solid #e2e8f0 !important;
    padding: 24px 30px !important;
  }
  
  .upgrade-modal .modal-header h5 {
    font-weight: 700 !important;
    color: #1e293b !important;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .upgrade-modal .modal-header h5 svg {
    color: #EF9F27;
  }
  
  .upgrade-modal .selected-plan {
    background: #E6F1FB !important;
    border-radius: 16px !important;
    padding: 24px !important;
    margin-bottom: 24px !important;
    text-align: center;
    border: 1px solid rgba(24, 95, 165, 0.1) !important;
  }
  
  .upgrade-modal .selected-plan-title {
    font-size: 14px;
    font-weight: 600;
    color: #0E3A61;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
  }
  
  .upgrade-modal .selected-plan-price {
    font-size: 36px;
    font-weight: 800;
    color: #185FA5;
    margin-bottom: 4px;
  }
  
  .upgrade-modal .selected-plan-duration {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 16px;
  }
  
  .upgrade-modal .features-list {
    text-align: left;
    display: inline-block;
    margin: 0 auto;
    padding: 0;
  }
  
  .upgrade-modal .features-list li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #334155;
    margin-bottom: 8px;
  }
  
  .upgrade-modal .features-list li svg {
    color: #16a34a;
  }
`;

const MembershipManagement: React.FC = () => {
  const navigate = useNavigate();
  const [membershipInfo, setMembershipInfo] = useState<MembershipInfo | null>(null);
  const [availableTiers, setAvailableTiers] = useState<MembershipTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState<MembershipTier | null>(null);
  const [phoneVisible, setPhoneVisible] = useState(false);
  const [togglingPhone, setTogglingPhone] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly'>('monthly');
  const [error, setError] = useState<string | null>(null);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [cancellingSub, setCancellingSub] = useState(false);
  const [paymentGateway, setPaymentGateway] = useState<'paddle' | 'stripe'>('paddle');
  const [transactionStatus, setTransactionStatus] = useState<{
    hasPaidMembership: boolean;
    transactionActive: boolean;
    transaction: any;
  }>({
    hasPaidMembership: false,
    transactionActive: false,
    transaction: null
  });

  const handleCancelSubscription = async () => {
    const result = await Swal.fire({
      title: 'Cancel Auto-Renewal?',
      text: 'Are you sure you want to cancel your recurring subscription? You will keep full access to all features until the end of your current billing period, and no further automatic charges will occur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Cancel Auto-Renewal',
      cancelButtonText: 'Keep Subscription',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    });

    if (result.isConfirmed) {
      setCancellingSub(true);
      try {
        const response = await fetch(`${API_BASE_URL}/membership/cancel`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('skillseek_talent_token')}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        if (data.success) {
          await fetchMembershipInfo();
          Swal.fire({
            title: 'Cancellation Scheduled',
            text: data.message || 'Auto-renewal has been turned off. Your plan remains active until the end of the current billing period.',
            icon: 'info',
            confirmButtonColor: '#185FA5'
          });
        } else {
          Swal.fire({
            title: 'Cancellation Error',
            text: data.message || 'Failed to cancel subscription.',
            icon: 'error',
            confirmButtonColor: '#dc3545'
          });
        }
      } catch (err) {
        console.error('Error cancelling subscription:', err);
        Swal.fire({
          title: 'Error',
          text: 'An error occurred while contacting the server.',
          icon: 'error',
          confirmButtonColor: '#dc3545'
        });
      } finally {
        setCancellingSub(false);
      }
    }
  };


  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    if (showUpgradeModal) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.height = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    };
  }, [showUpgradeModal]);

  const fetchTransactionStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/membership/transaction-check`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_talent_token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setTransactionStatus({
          hasPaidMembership: data.hasPaidMembership || false,
          transactionActive: data.hasPaidMembership && data.transaction && data.transaction.isActive,
          transaction: data.transaction || null
        });
        
        console.log('Transaction status loaded:', {
          hasPaidMembership: data.hasPaidMembership,
          transactionActive: data.hasPaidMembership && data.transaction && data.transaction.isActive,
          transaction: data.transaction,
          fullResponse: data
        });
      }
    } catch (error) {
      console.error('Error fetching transaction status:', error);
      setTransactionStatus({
        hasPaidMembership: false,
        transactionActive: false,
        transaction: null
      });
    }
  };

  const [membershipHistory, setMembershipHistory] = useState<MembershipHistoryItem[]>([]);

  const fetchMembershipHistory = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/membership/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_talent_token')}`
        }
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.history)) {
        setMembershipHistory(data.history);
      }
    } catch (err) {
      console.error('Error fetching membership history:', err);
    }
  };

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchMembershipInfo(),
        fetchAvailableTiers(),
        fetchTransactionStatus(),
        fetchMembershipHistory()
      ]);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load membership data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembershipInfo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/membership/my-membership`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_talent_token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setMembershipInfo(data.membership);
        setPhoneVisible(data.membership.phone_visible_to_customers);
      } else {
        throw new Error(data.message || 'Failed to fetch membership info');
      }
    } catch (error) {
      console.error('Error fetching membership info:', error);
      throw error;
    }
  };

  const fetchAvailableTiers = async () => {
    try {
      console.log('Fetching subscription plans...');
      const plansRes = await fetch(`${API_BASE_URL}/subscription-plans`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_talent_token')}`
        }
      });
      const plansData = await plansRes.json();
      
      if (plansData.success && Array.isArray(plansData.plans) && plansData.plans.length > 0) {
        const formattedTiers: MembershipTier[] = plansData.plans.map((plan: any) => ({
          id: plan.id,
          name: (plan.plan_name || 'premium').toLowerCase(),
          display_name: plan.plan_name || 'Premium',
          description: plan.description || '',
          price: parseFloat(plan.amount),
          duration_months: plan.billing_interval === 'yearly' ? 12 : (plan.billing_interval === 'hourly' ? 0 : 1),
          service_limit: -1,
          phone_visible: true,
          verified_badge: true,
          priority_support: true,
          enhanced_visibility: true,
          features: Array.isArray(plan.features) ? plan.features : [],
          paddle_price_id: plan.paddle_price_id,
          currency: plan.currency || 'USD',
          billing_interval: plan.billing_interval
        }));
        console.log('Dynamic active plans:', formattedTiers);
        setAvailableTiers(formattedTiers);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/membership/tiers`);
      const data = await response.json();
      
      if (data.success && Array.isArray(data.tiers)) {
        const formattedTiers: MembershipTier[] = data.tiers.map((tier: any) => ({
          id: tier.id,
          name: tier.name.toLowerCase(),
          display_name: tier.display_name,
          description: tier.description,
          price: parseFloat(tier.price),
          duration_months: tier.duration_months,
          service_limit: tier.service_limit,
          phone_visible: tier.phone_visible,
          verified_badge: tier.verified_badge,
          priority_support: tier.priority_support,
          enhanced_visibility: tier.enhanced_visibility,
          features: Array.isArray(tier.features) ? tier.features : []
        }));
        setAvailableTiers(formattedTiers);
      }
    } catch (error) {
      console.error('Error fetching tiers:', error);
      throw error;
    }
  };

  const handleUpgrade = async (transactionId: string, gateway: string = paymentGateway) => {
    setUpgrading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/membership/upgrade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_talent_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tierName: selectedTier?.name,
          paymentMethod: gateway,
          transactionId,
          amount: selectedTier?.price
        })
      });
      
      const data = await response.json();
      if (data.success) {
        await fetchMembershipInfo();
        await fetchTransactionStatus();
        setShowUpgradeModal(false);
        setSelectedTier(null);
        Swal.fire({
          title: 'Success!',
          text: 'Membership upgraded successfully!',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745'
        });
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Upgrade failed',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('Error upgrading membership:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Upgrade failed. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setUpgrading(false);
    }
  };

  const handleUpgradeClick = (tier: MembershipTier) => {
    console.log('Upgrade clicked for tier:', tier);
    navigate('/talent/membership/select', {
      state: {
        selectedTierId: tier.id,
        selectedTierName: tier.name,
        selectedTierDisplay: tier.display_name,
        autoOpenPayment: true
      }
    });
  };

  // Price calculation (single monthly plan)
  const calculatePrice = (basePrice: number) => basePrice.toString();

  const togglePhoneVisibility = async () => {
    setTogglingPhone(true);
    try {
      const response = await fetch('/api/membership/toggle-phone-visibility', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_talent_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          visible: !phoneVisible
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setPhoneVisible(data.phoneVisible);
      } else {
        alert(data.message || 'Failed to toggle phone visibility');
      }
    } catch (error) {
      console.error('Error toggling phone visibility:', error);
      alert('Failed to toggle phone visibility');
    } finally {
      setTogglingPhone(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-lg">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center text-red-500">
          <XCircle size={48} className="mx-auto mb-4" />
          <p>{error}</p>
          <button 
            className="mt-4 btn btn-outline-dark"
            onClick={loadInitialData}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!membershipInfo || !availableTiers.length) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center text-gray-500">
          <XCircle size={48} className="mx-auto mb-4 text-red-500" />
          <p>Unable to load membership information. Please try refreshing the page.</p>
          <button 
            className="mt-4 btn btn-outline-dark"
            onClick={loadInitialData}
          >
            Refresh Data
          </button>
        </div>
      </div>
    );
  }

  const currentTier = availableTiers.find(t => t.name === 'premium');

  // Check if the latest transaction has expired
  const isTransactionExpired = Boolean(
    transactionStatus.transaction?.endDate && 
    new Date(transactionStatus.transaction.endDate).getTime() <= Date.now()
  );

  // Check if worker membership profile end date has expired
  const isMembershipExpired = Boolean(
    membershipInfo?.membership_end_date && 
    new Date(membershipInfo.membership_end_date).getTime() <= Date.now()
  );

  // A talent has active membership if either:
  // 1. They have an active completed transaction whose end date is in the future
  // 2. OR their worker membership status is 'active' and end date is in the future
  const hasPurchasedMembership = Boolean(
    (transactionStatus.hasPaidMembership && transactionStatus.transactionActive && transactionStatus.transaction && !isTransactionExpired) ||
    (membershipInfo?.membership_status === 'active' && !isMembershipExpired)
  );
  
  // Check if membership is expired or needs renewal
  const needsRenewal = !hasPurchasedMembership && (
    isTransactionExpired ||
    isMembershipExpired ||
    membershipInfo?.membership_status === 'expired' ||
    (transactionStatus.hasPaidMembership && !transactionStatus.transactionActive)
  );
  
  // Check if user has no membership (no paid transaction)
  const hasNoMembership = !hasPurchasedMembership;

  console.log('MembershipManagement calculated states:', {
    transactionStatus,
    hasPurchasedMembership,
    needsRenewal,
    hasNoMembership
  });

  const formatDateStr = (dateInput: any): string => {
    if (!dateInput) return 'N/A';
    try {
      const dateObj = new Date(dateInput);
      if (isNaN(dateObj.getTime())) return String(dateInput);
      
      const hasTime = dateObj.getHours() !== 0 || dateObj.getMinutes() !== 0 || dateObj.getSeconds() !== 0;
      
      if (hasTime) {
        return dateObj.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      }

      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return String(dateInput);
    }
  };

  const purchasedTierName = (
    transactionStatus.transaction?.tierName || 
    transactionStatus.transaction?.planName || 
    membershipInfo?.membership_tier_name || 
    membershipInfo?.membership_tier_display ||
    ''
  ).toString().toLowerCase().trim();

  const purchasedAmount = (transactionStatus.transaction?.amount !== undefined && transactionStatus.transaction?.amount !== null && !isNaN(parseFloat(transactionStatus.transaction?.amount)))
    ? parseFloat(transactionStatus.transaction.amount)
    : null;

  const isTierMatch = (tier: MembershipTier) => {
    if (purchasedAmount !== null && purchasedAmount > 0 && Math.abs(tier.price - purchasedAmount) < 0.01) {
      return true;
    }
    if (!purchasedTierName) return false;
    const tierNameLower = (tier.name || '').toLowerCase().trim();
    const tierDisplayLower = (tier.display_name || '').toLowerCase().trim();

    if (purchasedTierName === tierNameLower || purchasedTierName === tierDisplayLower) {
      return true;
    }

    const cleanPurchased = purchasedTierName.replace(/[^a-z0-9]/g, '');
    const cleanTierName = tierNameLower.replace(/[^a-z0-9]/g, '');
    const cleanTierDisplay = tierDisplayLower.replace(/[^a-z0-9]/g, '');

    return (
      (cleanPurchased.length > 2 && cleanTierName.length > 2 && cleanPurchased === cleanTierName) ||
      (cleanPurchased.length > 2 && cleanTierDisplay.length > 2 && cleanPurchased === cleanTierDisplay)
    );
  };

  const matchedTier = availableTiers.find(isTierMatch);

  const activeUserTier: MembershipTier = (hasPurchasedMembership && transactionStatus.transaction)
    ? {
        id: matchedTier?.id || 9999,
        name: (transactionStatus.transaction.tierName || matchedTier?.name || 'custom').toLowerCase(),
        display_name: transactionStatus.transaction.tierDisplayName || transactionStatus.transaction.tierName || matchedTier?.display_name || 'Active Plan',
        description: matchedTier?.description || 'Your active subscription plan',
        price: (transactionStatus.transaction.amount !== undefined && transactionStatus.transaction.amount !== null && !isNaN(parseFloat(transactionStatus.transaction.amount)) && parseFloat(transactionStatus.transaction.amount) > 0)
          ? parseFloat(transactionStatus.transaction.amount)
          : (matchedTier?.price || 0),
        duration_months: matchedTier?.duration_months || 1,
        billing_interval: matchedTier?.billing_interval || transactionStatus.transaction?.billingInterval || 'monthly',
        currency: matchedTier?.currency || 'USD',
        service_limit: matchedTier?.service_limit ?? -1,
        phone_visible: matchedTier?.phone_visible ?? true,
        verified_badge: matchedTier?.verified_badge ?? true,
        priority_support: matchedTier?.priority_support ?? true,
        enhanced_visibility: matchedTier?.enhanced_visibility ?? true,
        features: (matchedTier?.features && matchedTier.features.length > 0) 
          ? matchedTier.features 
          : ["Unlimited service listings", "Verified badge display", "Phone number visibility", "Enhanced profile visibility", "Priority customer support"]
      }
    : (matchedTier || availableTiers[0]);

  const activePlanName = activeUserTier ? activeUserTier.display_name : 'Active Plan';

  const activeTierFeatures = Array.isArray(activeUserTier?.features) ? activeUserTier.features : [];
  const activeTierFeaturesLower = activeTierFeatures.map(f => (f || '').toString().toLowerCase().trim());

  const hasUnlimitedFeature = activeTierFeaturesLower.includes('full access') || 
    activeTierFeaturesLower.some(f => f.includes('unlimited service') || f.includes('unlimited listings'));

  const hasServiceListingAccess = hasUnlimitedFeature || 
    activeTierFeaturesLower.some(f => f.includes('service listing') || f.includes('service listings'));

  const numericLimit = hasUnlimitedFeature 
    ? -1 
    : (activeUserTier?.service_limit && activeUserTier.service_limit > 0 
        ? activeUserTier.service_limit 
        : (hasServiceListingAccess ? 5 : 0));

  const isUnlimitedService = numericLimit === -1;
  const serviceLimitDisplay = isUnlimitedService ? '∞' : numericLimit;
  const servicesUsedCount = membershipInfo?.services_used || 0;
  const servicesRemainingDisplay = isUnlimitedService 
    ? 'Unlimited' 
    : `${Math.max(0, numericLimit - servicesUsedCount)}`;

  return (
    <div className="pricing-page">
      <div className="container pricing-section">
        <h1 className="fw-bold text-center">Membership</h1>
        <p className="text-muted text-center">Enhance your profile with premium features</p>

        {/* Current Membership Status - Show at the top */}
        <div className="membership-status-card">
          <h3 className="h4 font-weight-bold text-dark mb-4">Current Membership Status</h3>
          <div className="row g-3">
            <div className="col-md-4">
              <div className="text-center p-3 membership-stat-box">
                <div className="h3 font-weight-bold stat-val-primary">{servicesUsedCount}</div>
                <div className="text-muted small">Services Used</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center p-3 membership-stat-box">
                <div className="h3 font-weight-bold stat-val-success">
                  {hasPurchasedMembership ? serviceLimitDisplay : '0'}
                </div>
                <div className="text-muted small">Service Limit</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center p-3 membership-stat-box">
                <div className="h3 font-weight-bold stat-val-warning">
                  {hasPurchasedMembership ? servicesRemainingDisplay : '0'}
                </div>
                <div className="text-muted small">Remaining</div>
              </div>
            </div>
          </div>
          
          <div className="membership-info-box mt-4" style={{ backgroundColor: hasPurchasedMembership ? '#f0f9ff' : '#f8fafc', border: hasPurchasedMembership ? '1px solid #bae6fd' : '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
            <div className="row g-3">
              <div className="col-md-6">
                <span className="text-muted fw-semibold">Current Plan:</span>{' '}
                <strong className="text-dark ms-1">
                  {hasPurchasedMembership ? activePlanName : 'No Active Plan'}
                </strong>
                {hasPurchasedMembership ? (
                  <span className="badge bg-success ms-2 px-2 py-1">Active</span>
                ) : (
                  <span className="badge bg-secondary ms-2 px-2 py-1">Not Subscribed</span>
                )}
                {needsRenewal && (
                  <span className="badge bg-warning ms-2 px-2 py-1">Expired</span>
                )}
              </div>
              <div className="col-md-6">
                <span className="text-muted fw-semibold">Status:</span>{' '}
                <strong className="text-dark ms-1">
                  {hasPurchasedMembership ? `${activePlanName} Active` : 'No Membership Purchased'}
                </strong>
              </div>
              <div className="col-md-6">
                <span className="text-muted fw-semibold">Start Date:</span>{' '}
                <strong className="text-dark ms-1">
                  {hasPurchasedMembership ? formatDateStr(transactionStatus.transaction?.startDate || membershipInfo?.membership_start_date) : 'N/A'}
                </strong>
              </div>
              <div className="col-md-6">
                <span className="text-muted fw-semibold">End Date:</span>{' '}
                <strong className="text-dark ms-1">
                  {hasPurchasedMembership ? formatDateStr(transactionStatus.transaction?.endDate || membershipInfo?.membership_end_date) : 'N/A'}
                </strong>
              </div>
            </div>
            {hasPurchasedMembership && (
              <div className="mt-3 pt-3 border-top d-flex align-items-center justify-content-between flex-wrap gap-2">
                {membershipInfo?.cancel_at_period_end ? (
                  <div className="badge bg-warning bg-opacity-10 text-dark border border-warning px-3 py-2 fw-semibold d-flex align-items-center gap-2">
                    <Clock size={16} className="text-warning" />
                    Auto-Renewal is OFF. Subscription will cancel on {formatDateStr(transactionStatus.transaction?.endDate || membershipInfo?.membership_end_date)}.
                  </div>
                ) : (
                  <>
                    <span className="small text-muted">Recurring billing is active.</span>
                    {/* <button
                      className="btn btn-outline-danger btn-sm fw-semibold"
                      style={{ borderRadius: '8px' }}
                      onClick={handleCancelSubscription}
                      disabled={cancellingSub}
                    >
                      {cancellingSub ? 'Cancelling...' : 'Cancel Auto-Renewal'}
                    </button> */}
                  </>
                )}
              </div>
            )}
          </div>

          {!hasPurchasedMembership && (
            <div className="alert alert-warning border-0 shadow-sm mt-4 mb-0 d-flex align-items-center gap-3 p-3" style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '12px', color: '#8c6b00' }}>
              <div>
                <strong className="d-block mb-1">You do not have an active membership plan</strong>
                <span className="small">Please choose a plan below to activate your membership and unlock premium features.</span>
              </div>
            </div>
          )}
        </div>

        {/* Available / Active Plans Header with Purchase Button */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h3 className="h4 font-weight-bold text-dark m-0">
              {hasPurchasedMembership ? (showAllPlans ? 'All Subscription Plans' : 'Your Active Plan') : 'Available Subscription Plans'}
            </h3>
            <small className="text-muted">
              {hasPurchasedMembership ? (!showAllPlans ? 'You are currently subscribed to this plan.' : 'Explore available plans') : 'Select a plan below to activate your membership.'}
            </small>
          </div>
          <button 
            className="btn fw-bold d-flex align-items-center gap-2 px-4 py-2"
            style={{ 
              borderRadius: '12px', 
              backgroundColor: '#185FA5', 
              color: '#ffffff',
              border: 'none',
              boxShadow: '0 4px 12px rgba(24, 95, 165, 0.25)',
              transition: 'all 0.2s ease'
            }}
            onClick={() => navigate('/talent/membership/select')}
          >
            <Crown size={18} />
            + Purchase New / Switch Plan
          </button>
        </div>

        {(() => {
          const displayedTiers = (hasPurchasedMembership && activeUserTier && !showAllPlans) ? [activeUserTier] : availableTiers;

          return (
            <div className="row justify-content-center g-4">
              {displayedTiers.map((tier) => {
                const isCurrentPlan = hasPurchasedMembership && (tier.id === activeUserTier.id || isTierMatch(tier) || tier.display_name === activeUserTier.display_name);

                return (
                  <div 
                    className={displayedTiers.length >= 3 ? 'col-lg-4 col-md-6 col-12' : (displayedTiers.length === 2 ? 'col-md-6 col-12' : 'col-md-6 col-lg-5 col-12')} 
                    key={tier.id}
                  >
                    <div className="card pricing-card popular h-100 mb-4 d-flex flex-column" style={{ borderColor: isCurrentPlan ? '#16a34a' : '#e2e8f0' }}>
                      <div className="card-body d-flex flex-column h-100">
                        <h5 className="d-flex justify-content-between align-items-center">
                          <span>{tier.display_name}</span>
                          {isCurrentPlan && (
                            <span className="badge bg-success">Current Plan</span>
                          )}
                        </h5>
                        <h2 className="fw-bold">{getCurrencySymbol(tier.currency || '')}{tier.price % 1 === 0 ? tier.price.toFixed(0) : tier.price.toFixed(2)}</h2>
                        <p className="text-muted">Per {
                          tier.billing_interval === 'hourly' ? 'hour' : 
                          tier.billing_interval === 'daily' ? 'day' : 
                          tier.billing_interval === 'weekly' ? 'week' : 
                          tier.duration_months === 12 ? 'year' : 
                          'month'
                        }</p>
                        <ul className="list-unstyled flex-grow-1">
                          {tier.features && tier.features.length > 0 ? (
                            tier.features.map((feat, idx) => (
                              <li key={idx}>✔ {feat}</li>
                            ))
                          ) : (
                            <>
                              <li>✔ Unlimited service listings</li>
                              <li>✔ Verified badge display</li>
                              <li>✔ Phone number visibility</li>
                              <li>✔ Enhanced profile visibility</li>
                              <li>✔ Priority customer support</li>
                            </>
                          )}
                        </ul>
                        <div className="mt-auto pt-3">
                          {isCurrentPlan ? (
                            <div className="alert alert-success border-0 shadow-sm mb-0" style={{ backgroundColor: '#ecfdf5', color: '#065f46', borderRadius: '12px', padding: '16px' }}>
                              <strong className="d-block mb-1">Active Membership</strong>
                              <p className="mb-0 small" style={{ color: '#047857' }}>
                                Your premium features are active until <strong>{formatDateStr(transactionStatus.transaction?.endDate || membershipInfo.membership_end_date)}</strong>
                              </p>
                            </div>
                          ) : (
                            <button 
                              className="brand-btn-gradient w-100"
                              onClick={() => handleUpgradeClick(tier)}
                            >
                              Subscribe {getCurrencySymbol(tier.currency || '')}{tier.price % 1 === 0 ? tier.price.toFixed(0) : tier.price.toFixed(2)}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Previous Subscriptions & Billing History Section */}
        <div className="card border-0 shadow-sm mt-5 mb-4" style={{ borderRadius: '16px', background: '#ffffff', overflow: 'hidden' }}>
          <div className="card-header bg-white border-bottom p-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h4 className="fw-bold m-0 text-dark d-flex align-items-center gap-2" style={{ fontSize: '20px' }}>
                <Clock size={22} style={{ color: '#185FA5' }} /> Previous Subscriptions & Billing History
              </h4>
              <p className="text-muted small m-0 mt-1">View your past subscription plans, transaction IDs, and billing dates.</p>
            </div>
            <span className="badge bg-light text-dark border px-3 py-2 fw-semibold" style={{ fontSize: '13px' }}>
              {membershipHistory.length} Transaction{membershipHistory.length === 1 ? '' : 's'}
            </span>
          </div>

          <div className="card-body p-0">
            {membershipHistory.length === 0 ? (
              <div className="text-center py-5 px-4 text-muted">
                <CreditCard size={48} className="mb-2 opacity-50" />
                <p className="fw-semibold mb-0">No past subscription transactions found.</p>
                <small>When you purchase or upgrade a membership plan, your billing records will appear here.</small>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0" style={{ fontSize: '14px' }}>
                  <thead className="table-light">
                    <tr>
                      <th className="ps-4">Plan Name</th>
                      <th>Transaction ID</th>
                      <th>Amount</th>
                      <th>Payment Method</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membershipHistory.map((item) => (
                      <tr key={item.id}>
                        <td className="ps-4 fw-bold text-dark">
                          {item.planName || 'Subscription Plan'}
                          <span className="badge bg-light text-secondary ms-2 border text-capitalize" style={{ fontSize: '11px' }}>
                            {item.billingInterval || 'monthly'}
                          </span>
                        </td>
                        <td className="font-monospace text-muted small">
                          {item.transactionId}
                        </td>
                        <td className="fw-bold" style={{ color: '#185FA5' }}>
                          {getCurrencySymbol(item.currency || 'USD')}{item.amount.toFixed(2)}
                        </td>
                        <td>
                          <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2 py-1 text-capitalize">
                            {item.paymentMethod || 'Paddle'}
                          </span>
                        </td>
                        <td>{item.startDate ? new Date(item.startDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</td>
                        <td>{item.endDate ? new Date(item.endDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</td>
                        <td>
                          {item.status === 'completed' || item.status === 'active' ? (
                            <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-2 py-1">
                              Completed
                            </span>
                          ) : (
                            <span className="badge bg-secondary px-2 py-1">
                              {item.status}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipManagement; 